import React, { useState } from "react";
import { Typography, Button, Box, Stack } from "@mui/material";
import { CommonLayout, FormWrapper } from "../components/CommonLayout";
import NotificationBanner from "../components/common/NotificationBanner";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { sendForgotPasswordEmail } from "../services/password.service";
import type { AxiosError } from "axios";

const ForgotPasswordPage: React.FC = () => {
  const [notification, setNotification] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Por favor ingresa un correo electrónico válido")
      .required("Por favor ingresa tu correo electrónico"),
  });

  const handleSubmit = async (values: { email: string }, { resetForm }: any) => {
    setIsLoading(true);
    try {
      await sendForgotPasswordEmail(values.email.trim().toLowerCase());

      setNotification({
        open: true,
        type: "success",
        title: "Correo enviado",
        message: "Te enviamos instrucciones para cambiar tu contraseña.",
      });
      resetForm();
    } catch (e) {
      const err = e as AxiosError<any>;
      const status = err.response?.status;
      let title = "Error";
      let message = "No se pudo enviar el correo. Intenta nuevamente.";

      if (!err.response) {
        title = "Sin conexión";
        message = "No se pudo contactar al servidor. Verifica tu conexión.";
      } else if (status === 400) {
        title = "Solicitud inválida";
        message = (err.response.data as any)?.message ?? "Revisa el correo ingresado.";
      } else if (status === 404) {
        title = "Correo no encontrado";
        message = "El correo ingresado no está registrado.";
      } else if (status === 429) {
        title = "Límite alcanzado";
        message = "Demasiadas solicitudes. Intenta nuevamente más tarde.";
      } else if (status && status >= 500) {
        title = "Error del servidor";
        message = "Estamos teniendo problemas. Intenta más tarde.";
      }

      setNotification({ open: true, type: "error", title, message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CommonLayout>
      <FormWrapper elevation={3}>
        <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Stack spacing={1}>
              <Typography fontSize={20} fontWeight={700}>
                Olvide mi Contraseña
              </Typography>
              <Typography fontSize={14} color="#bdbdbd" fontWeight={400}>
                Ingresa tu e-mail y te enviaremos instrucciones para cambiar tu contraseña.
              </Typography>
            </Stack>
          </Box>
          <Formik initialValues={{ email: "" }} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ errors, touched, isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <Stack spacing={2}>
                  <Box>
                    <Field
                      name="email"
                      type="email"
                      placeholder="E-mail"
                      autoComplete="username"
                      style={{
                        width: "100%",
                        padding: "12px",
                        fontSize: "16px",
                        border: errors.email && touched.email ? "2px solid #d32f2f" : "1px solid #ccc",
                        borderRadius: "6px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {errors.email && touched.email && (
                      <Typography color="error" fontSize={14} mt={1} textAlign="left">
                        {errors.email}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      py: 1.5,
                      fontWeight: "bold",
                      backgroundColor: "#002E74",
                      color: "white",
                      fontSize: 14,
                      "&:hover": { backgroundColor: "#001F4D" },
                    }}
                    disabled={isLoading || isSubmitting}
                  >
                    {isLoading || isSubmitting ? "Procesando..." : "Cambiar Contraseña"}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
          <NotificationBanner
            type={notification.type as "error" | "success" | "warning" | "info"}
            title={notification.title}
            message={notification.message}
            open={notification.open}
            onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          />
        </Stack>
      </FormWrapper>
    </CommonLayout>
  );
};

export default ForgotPasswordPage;
